import React from "react";
import _ from "lodash";

import {Grid} from "@material-ui/core";

import {FIELD_TYPES, getSchemeFields, schemeFlattenFields} from "../../forms_schemes";
import InputFormElement, {CurrencyNumberFormat} from "../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement";
import {newDesignInputCustomClassesViewOnly} from "../../../constants";
import {newDesignInputCustomClasses} from "../../../../RiskProfiling/constants";
import CalendarFormElement
  from "../../../../RiskProfiling/components/StepContent/components/formElement/CalendarFormElement";
import SelectFormElementV2
  from "../../../../RiskProfiling/components/StepContent/components/formElement/SelectFormElementV2";
import {dateToMoment, momentToDate} from "../../utils";
import NonFieldErrors from "../../../../../components/AssetModal/components/NonFieldErrors";
import {
  NumberFormat,
  PercentageNumberFormat
} from "../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement/InputFormElement";
import Toggle, {OFF_LABEL, ON_LABEL} from "../Toggle";
import DisabledFieldTooltipWrapper from "../DisabledCategoryTooltipWrapper";


const renderField = (field, model, handleModelChange, errors, viewOnly, inputClasses, isCreate) => {
  const {label, modelField, dataId, required, disabled} = field;

  const commonProps = {
    label,
    value: _.get(model, modelField),
    error: _.get(errors, modelField),
    dataId: dataId || `${modelField}-input`,
    onChange: handleModelChange(field),
    required: !viewOnly && !!required,
    disabled: viewOnly || disabled,
    custom_classes: inputClasses
  };

  switch (field.type) {
    case FIELD_TYPES.amount:
      return (
        <InputFormElement
          {...commonProps}
          inputComponent={CurrencyNumberFormat}
          inputProps={{inputProps: {allowNegative: false}}}
        />
      );
    case FIELD_TYPES.number:
      return (
        <InputFormElement
          {...commonProps}
          inputComponent={NumberFormat}
          inputProps={{inputProps: {allowNegative: false}}}
        />
      );
    case FIELD_TYPES.percentage:
      return (
        <InputFormElement
          {...commonProps}
          inputComponent={PercentageNumberFormat}
          inputProps={{inputProps: {allowNegative: false, isAllowed: (values) => {
            const {floatValue} = values;
            return !floatValue || floatValue <= 100;
          }}}}
        />
      );
    case FIELD_TYPES.date:
      return (
        <CalendarFormElement
          {...commonProps}
          disableFuture={field.disableFuture}
          minDate={!!field.minDateField && model[field.minDateField] || undefined}
          minError={field.minError}
          maxDate={!!field.maxDateField && model[field.maxDateField] || undefined}
          maxError={field.maxError}
        />
      );
    case FIELD_TYPES.select:
      // in Contract API: options are strings but real values could be int. As a workaround - convert value to str
      if(_.isNumber(commonProps.value)){
        commonProps.value = String(commonProps.value)
      }

      return (
        <SelectFormElementV2
          {...commonProps}
          options={field.optionsGetter ? field.optionsGetter(viewOnly) : field.options}
          newDesign
          addEmptyOption={!required}
        />
      );
    case FIELD_TYPES.toggle:
      return (
        viewOnly
          ? <InputFormElement
            {...commonProps}
            inputComponentProps={{size: field.readOnlyInputSize}}
            value={commonProps.value ? ON_LABEL : OFF_LABEL}
          />
          : <Toggle {...commonProps} />
      )
    case FIELD_TYPES.group:
      return (
        <Grid container spacing={1}>
          {field.fields.map(f => renderFieldComponent(f, model, handleModelChange, errors, viewOnly, inputClasses, isCreate))}
        </Grid>
      )
    default:
      return <InputFormElement {...commonProps} />
  }
};

const renderFieldComponent = (f, model, handleModelChange, errors, viewOnly, inputClasses, isCreate) => {
  return !!f.hideOnViewOnly && viewOnly ? null : (
    <DisabledFieldTooltipWrapper key={f.dataId || f.modelField} title={f.disabled && isCreate && f.disabledReasonForCreate}>
      <Grid item {...f.sizes}>
        {renderField(f, model, handleModelChange, errors, viewOnly, inputClasses, isCreate)}
      </Grid>
    </DisabledFieldTooltipWrapper>
  )
}

const getValueForFrontEnd = (field, defaults) => {
  let value = _.get(defaults, field.modelField);

  if(!value && field.hasOwnProperty('value')){
    value = field.value;
  }

  switch (field.type) {
    case FIELD_TYPES.date:
      return dateToMoment(value);
    default:
      return value
  }
};

const getValueForBackEnd = (field, value) => {
  switch (field.type) {
    case FIELD_TYPES.date:
      return momentToDate(value);
    default:
      return value
  }
};

const DynamicForm = ({schema, defaults, handleFormChange, errors={}, viewOnly}) => {
  const inputClasses = viewOnly ? newDesignInputCustomClassesViewOnly : newDesignInputCustomClasses;

  const [model, setModel] = React.useState(() =>
    schemeFlattenFields(schema).reduce((acc, f) => {
      _.set(acc, f.modelField, getValueForFrontEnd(f, defaults));
      return acc;
    }, {})
  );

  const resolvedSchemeFields = React.useMemo(() => {
    return getSchemeFields(schema, model, defaults);
  }, [..._.get(schema, 'dynamic.deps', []).map(d => model[d])]);

  const handleModelChange = (field) => (value) => {
    const newState = {...model};
    const newValue = value;
    const fieldName = field.modelField;
    _.set(newState, fieldName, newValue);

    setModel(newState);
    handleFormChange && handleFormChange(fieldName, getValueForBackEnd(field, newValue));
  };

  return (
    <>
      {resolvedSchemeFields.map(f => renderFieldComponent(f, model, handleModelChange, errors, viewOnly, inputClasses, !defaults.id))}
      {!!errors['non_field_errors'] && <NonFieldErrors errors={errors['non_field_errors']}/>}
    </>
  )
};

export default DynamicForm;